import {Client} from "voximplant-websdk/Client";
import * as VoxImplant from 'voximplant-websdk';
import {Call} from "voximplant-websdk/Call/Call";
import {EventHandlers} from "voximplant-websdk/EventHandlers";
import ErrorHandler from "../../../helpers/ErrorHandler";
import EventApi from "../../../api/EventsApi";
import {Endpoint} from "voximplant-websdk/Call/EndPoints/Endpoint";
import {LogRecord} from "voximplant-websdk/Structures";
import MessageHelper from "../../../helpers/MessageHelper";
import * as VideoConference from 'video-conference';
import DevicesManager from "./DevicesManager";
import {MediaRenderer} from "voximplant-websdk/Media/MediaRenderer";

export default class VideoManager implements VideoConference.VideoManagerInterface {
    private sdk: Client;
    private currentConf: Call | null = null;
    private logs: Array<string> = [];
    public devicesManager: DevicesManager|null = null;
    private accessGranted: boolean = false;

    constructor()
    {
        this.sdk = VoxImplant.getInstance();

        this.sdk.on(VoxImplant.Events.IncomingCall, (e: EventHandlers.IncomingCall) => {
            if (this.currentConf) {
                ErrorHandler.info('[WebSDk] You already have active call. Hangup;');
                e.call.hangup();
            } else {
                this.currentConf = e.call;
                this.bindCallCallbacks();
                this.currentConf.answer();
            }
            ErrorHandler.info(`[WebSDk] New incoming call with ID: ${e.call.id()}`);
        });

        this.sdk.on(VoxImplant.Events.MicAccessResult, (e: EventHandlers.MicAccessResult) => {
            if (e.result) {
                if (!this.accessGranted) {
                    this.accessGranted = true;
                    EventApi.triggerEvent(EventApi.DEVICES_ACCESS_GRANTED);
                }
            } else {
                ErrorHandler.error('You need to allow access to your cam and mic to join');
            }
        });

        this.bindLogsCallback();
    }

    public setDevicesManager(devicesManager: DevicesManager): void
    {
        this.devicesManager = devicesManager;
    }

    public async init(user: string): Promise<void>
    {
        const url = process.env.MIX_VOXIMPLANT_URL;
        let username = `${user}@${url}`;

        await this.connectToServer();

        EventApi.addEventListener(EventApi.LOGIN_WITH_TOKEN, (event: any) => {
            this.sdk.loginWithOneTimeKey(username, event.detail.token).then(() => {
                EventApi.triggerEvent(EventApi.VIDEO_CONFERENCE_READY);
            });
        });

        this.sdk.on(VoxImplant.Events.AuthResult, (e: EventHandlers.AuthResult) => {
            ErrorHandler.info(`AuthResult: ${e.result}`);
            ErrorHandler.info(`Auth code: ${e.code}`);
            if (e.result) {
                // Login is successful
            } else if (e.code === 302) {
                MessageHelper.sendMessage('getConferenceUserToken', {
                    'key': e.key,
                });
            } else {
                ErrorHandler.error(`Voximplant auth error. Code: ${e.code}`);
            }
        });

        await this.sdk.requestOneTimeLoginKey(username);
    }

    /**
     * Подключиться к видео-серверу
     */
    private async connectToServer()
    {
        // we must check current SDK state to prevent exception in case of repeated init and connect
        if (this.sdk.getClientState() !== VoxImplant.ClientState.CONNECTED) {
            // init SDK
            await this.sdk.init({
                localVideoContainerId: 'localUserVideo',
                remoteVideoContainerId: 'hiddenRemoteVideos',
                queueType: VoxImplant.QueueTypes.SmartQueue,
                showDebugInfo: true
            });
            ErrorHandler.info('[WebSDk] Init completed');
            // connecting to the Voximplant Cloud;
            // "false" argument disables checking UDP connection (for fastest connect)
            await this.sdk.connect();
            ErrorHandler.info('[WebSDk] Connected');
        }
    }

    /**
     * Отключиться от видео-сервера
     */
    public async disconnect(): Promise<void>
    {
        await this.sdk.disconnect();
    }

    /**
     * Поключиться к комнате
     * @param roomId
     * @param playerId
     */
    public connectToConf(roomId: string, playerId: string): void
    {
        this.currentConf = this.sdk.callConference({
            number: 'conf_' + roomId,
            video: {sendVideo: true, receiveVideo: true},
            extraHeaders: {
                // передаем в этом поле айди игрока для идентификации видеопотока
                'X-Display-Name': playerId,
            },
        });

        this.bindCallCallbacks();
    }

    /**
     * Установка callback'ов конференции
     * @private
     */
    private bindCallCallbacks(): void
    {
        if (!this.currentConf) {
            return;
        }

        this.currentConf.on(VoxImplant.CallEvents.Connected, (e: EventHandlers.IncomingCall) => {
            this.onCallConnected(e)
        });

        this.currentConf.on(VoxImplant.CallEvents.Disconnected, (e: EventHandlers.Disconnected) => {
            this.onCallDisconnected(e)
        });
        this.currentConf.on(VoxImplant.CallEvents.Failed, (e: EventHandlers.Failed) => {
            this.onCallFailed(e);
        });
        this.currentConf.on(VoxImplant.CallEvents.EndpointAdded, (e: EventHandlers.EndpointHandler) => {
            this.onEndpointAdded(e);
        });
    }

    /**
     * Установка callback'ов для логов
     * @private
     */
    private bindLogsCallback(): void {
        this.sdk.setLoggerCallback((log: LogRecord) => {
            if (this.logs.length >= 5000) {
                this.logs.shift();
            }

            this.logs.push(log.formattedText);
        });
    }

    /**
     * Начало звонка
     */
    private onCallConnected(e: EventHandlers.IncomingCall): void
    {
        if (!this.currentConf || !this.devicesManager) {
            return;
        }

        this.currentConf.sendVideo(this.devicesManager.getCameraStatus());
        this.devicesManager.getMicrophoneStatus() ? this.currentConf.unmuteMicrophone() : this.currentConf.muteMicrophone();

        let settings = this.currentConf.settings;

        if (typeof settings.video !== 'undefined' && typeof settings.video !== 'boolean') {
            let video = settings.video as VoxImplant.VideoFlags;

            if (video.sendVideo) {
                this.sdk.showLocalVideo(true);
            }
        }

        ErrorHandler.info(`[WebSDk] Call connected ID: ${e.call.id()}`);
        ErrorHandler.info(e);
        EventApi.triggerEvent('callConnected');
    }

    /**
     * Конец звонка
     */
    private onCallDisconnected(e: EventHandlers.Disconnected): void {
        this.currentConf = null;
        ErrorHandler.info(`[WebSDk] Call ended ID: ${e.call.id()}`);
    }

    /**
     * Возникновение ошибки
     */
    private onCallFailed(e: EventHandlers.Failed): void
    {
        this.currentConf = null;
        ErrorHandler.info(`[WebSDk] Call failed ID: ${e.call.id()}`);
        //Описание кодов https://voximplant.com/docs/references/websdk/voximplant/callevents#failed
        ErrorHandler.error(`Voximplant call failed: code ${e.code} reason: ${e.reason}`);
    }

    /**
     * Добавление эндпоинта
     */
    private onEndpointAdded(e: EventHandlers.EndpointHandler): void
    {
        e.endpoint.on(VoxImplant.EndpointEvents.Removed, (e: EventHandlers.EndpointMediaHandler) => {
            VideoManager.onEndpointRemoved(e);
        });
        e.endpoint.on(VoxImplant.EndpointEvents.RemoteMediaAdded, (e) => {
            VideoManager.onRemoteMediaAdded(e);
        });

        e.endpoint.on(VoxImplant.EndpointEvents.RemoteMediaRemoved, (e) => {
            VideoManager.onRemoteMediaRemoved(e);
        });

        if (e.endpoint.isDefault) {
            EventApi.triggerEvent('localVideoEndpointAdded', e.endpoint);
        }

        ErrorHandler.info(`[WebSDk] New endpoint ID:`);
        ErrorHandler.info(e.endpoint);

        EventApi.triggerEvent('endpointAdded', [e.endpoint]);
    }

    /**
     * При удалении эндпоинта
     */
    private static onEndpointRemoved(e: EventHandlers.EndpointMediaHandler)
    {
        ErrorHandler.info(`[WebSDk] Endpoint was removed ID: ${e.endpoint.id}`);
    }

    /**
     * Добавление медиа элемента
     */
    private static onRemoteMediaAdded(e: any)
    {
        EventApi.triggerEvent('remoteMediaAdded', [e.mediaRenderer]);
        ErrorHandler.info(`[WebSDk] New MediaRenderer ID: ${e.mediaRenderer.id} in ${e.endpoint.id}`);
    }

    /**
     * Удаление медиа элемента
     */
    private static onRemoteMediaRemoved(e: EventHandlers.EndpointMediaHandler)
    {
        EventApi.triggerEvent('remoteMediaRemoved', [e.mediaRenderer]);
        ErrorHandler.info(`[WebSDk] MediaRenderer was removed ID: ${e.mediaRenderer.id} in ${e.endpoint.id}`);
    }

    /**
     * Все эндпоинты
     */
    public get endpoints(): Array<Endpoint>
    {
        if (!this.currentConf) {
            return [];
        }
        return this.currentConf.getEndpoints();
    }

    /**
     * Изменить состояние камеры
     */
    public cameraToggle(cameraStatus: boolean): void
    {
        if (this.currentConf) {
            this.currentConf.sendVideo(cameraStatus);
        }

        const localVideoId = 'voximplantlocalvideo';
        if (cameraStatus) {
            if (!document.getElementById(localVideoId)) {
                this.sdk.showLocalVideo(true);
            }
        } else {
            if (document.getElementById(localVideoId)) {
                this.sdk.showLocalVideo(false);
            }
        }

        if (this.devicesManager) {
            this.devicesManager.setCameraStatus(cameraStatus);
        }
    }

    /**
     * Изменить состояние микрофона
     */
    public microphoneToggle(micStatus: boolean): void
    {
        if (this.currentConf) {
            if (micStatus) {
                this.currentConf.unmuteMicrophone();
            } else {
                this.currentConf.muteMicrophone();
            }
        }

        if (this.devicesManager) {
            this.devicesManager.setMicrophoneStatus(micStatus);
        }
    }

    /**
     * Изменить состояние аудио
     */
    public audioToggle(status: boolean): void
    {
        if (this.currentConf) {
            if (status) {
                this.currentConf.unmutePlayback();
            } else {
                this.currentConf.mutePlayback();
            }
        }

        if (this.devicesManager) {
            this.devicesManager.setAudioStatus(status);
        }
    }

    /**
     * Получение логов
     */
    public getLogs(): Array<string>
    {
        return this.logs;
    }

    /**
     * Проверка браузера
     */
    public checkBrowser(): boolean
    {
        return this.sdk.isRTCsupported();
    }

    /**
     * Текущая конференция
     */
    public getCurrentConf(): Call|null
    {
        return this.currentConf;
    }

    public render(videoHolders: Array<HTMLElement>): void
    {
        const endpoints = this.endpoints;
        let processed: Array<string> = [];

        endpoints.forEach((endpoint: Endpoint) => {
            const id = endpoint.displayName;

            if (!endpoint.isDefault && processed.indexOf(id) === -1) {
                processed.push(id);

                const filtered = videoHolders.filter(holder => holder.getAttribute('data-endpoint-id') === id);

                if (typeof filtered[0] !== 'undefined') {
                    const holder = filtered[0];
                    holder.innerHTML = '';
                    endpoint.mediaRenderers.forEach((mediaRenderer: MediaRenderer) => {
                        mediaRenderer.render(holder);
                    });
                }
            }
        });
    }

    public closeSettings(): void
    {
    }
}

